import { useContext, useCallback } from 'react';
import { LOW_RESULTS_THRESHOLD, LOW_RESULTS_COUNT } from '~/constants/srp';
import { useIsDealershipPage } from '~/context/dealership/StaticContext';
import InventoryContext from '~/context/srp/InventoryContext';
import { InventoryRequest } from '~/contracts/common';
import { InventoryResponse } from '~/services/srp/contracts';
import fetchLowInventory from '~/services/srp/fetchLowInventory';
import { logError } from '~/utilities/loggers';

export default function useGetLowInventory() {
  const { setSimilarVehicles } = useContext(InventoryContext);
  const isDealership = useIsDealershipPage();

  return useCallback(
    async (
      request: InventoryRequest,
      inventoryData: InventoryResponse,
      controller: AbortController
    ) => {
      try {
        const {
          inventory: {
            pagination: { totalMatchedInventory, totalMatchedPages },
            vehicles: inventoryVehicles
          }
        } = inventoryData;
        const lowInventory =
          totalMatchedPages === 1 &&
          inventoryVehicles.length < LOW_RESULTS_THRESHOLD;
        const noInventory = !totalMatchedInventory;
        const shouldFetchSimilarVehicles =
          (lowInventory || noInventory) && !isDealership;

        if (!shouldFetchSimilarVehicles) return;

        if (lowInventory) {
          request.pagination = {
            page: 1,
            pageSize: totalMatchedInventory + LOW_RESULTS_COUNT
          };
        }

        const {
          data: { inventory, searchRequestId }
        } = await fetchLowInventory(request, controller);

        const { vehicles: allSimilarVehicles } = inventory;
        const existingVehicleIds = inventoryVehicles.map(v => v.vehicleId);
        const deduplicatedVehicles = allSimilarVehicles
          .filter(v => !existingVehicleIds.includes(v.vehicleId))
          .slice(0, LOW_RESULTS_COUNT);
        setSimilarVehicles({
          searchRequestId,
          vehicles: deduplicatedVehicles
        });
      } catch (error) {
        logError(error, { location: 'Low Inventory' }, { controller });
      }
    },
    [isDealership, setSimilarVehicles]
  );
}
